"use client";

// Browser wiring for src/lib/account-handoff.ts — the ONLY place that touches `window`.
// Installed once by ExportSessionBridge, which already owns "what does the host know about the session".
//
// Two interception points, one handoff, neither depending on the Navigation API:
//   1. a capture-phase document click for real <a href> links (header My Tickets, LoginPopup rows, …);
//   2. `window.__SG_ACCOUNT_NAV(dest)`, the hook the generated design calls from its account actions
//      (Profile / My Wallet / Saved Items …; see SG_GO_ACCOUNT in design-sources.ts). It runs synchronously
//      inside the click handler, so the popup keeps its user activation in every browser.
// Both only act for: a signed-in root session, a CROSS-ORIGIN accounts origin (siblings included), and an ALLOWLISTED
// accounts path. Everything else is left completely alone (the hook answers false → plain navigation).

import type { HandoffIO, PopupHandle } from "@/lib/account-handoff";
import { accountsUrl } from "@/lib/account-links";
import { useCartStore } from "@/lib/cart-store";
import {
  accountTargetOf,
  cancelActiveHandoff,
  isWellShapedJwt,
  startAccountHandoff,
  startSsoAccountTransfer,
} from "@/lib/account-handoff";

function randomState(): string {
  const bytes = new Uint8Array(24);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
}

function accountsOrigin(): string | null {
  try {
    return new URL(accountsUrl("/")).origin;
  } catch {
    return null;
  }
}

/** True when a click on this accounts path should go through the popup bridge right now. */
function needsBridge(origin: string): boolean {
  if (!isWellShapedJwt(useCartStore.getState().session.jwt)) return false; // signed out: plain link → accounts login
  // Any DIFFERENT origin needs the bridge, sibling hosts included. The previous rule skipped the bridge whenever
  // sharesCookieScope() said the two hosts shared a cookie scope, and a signed-in staging root was observed arriving at the accounts
  // login page; whether a cookie is actually shared is not something this code can know. Same-origin keeps the plain navigation.
  return origin !== window.location.origin;
}

type AccountNavHook = (dest: string) => boolean;

export function installAccountHandoff(): () => void {
  const noop = () => undefined;
  if (typeof window === "undefined") return noop;
  const origin = accountsOrigin();
  if (!origin) return noop;

  const io: HandoffIO = {
    accountsOrigin: origin,
    open: (url) => {
      const w = window.open(url, "snowclub-account-bridge", "popup=yes,width=460,height=560");
      return (w as unknown as PopupHandle | null) ?? null;
    },
    listen: (handler) => {
      const fn = (ev: MessageEvent) => handler({ origin: ev.origin, source: ev.source, data: ev.data });
      window.addEventListener("message", fn);
      return () => window.removeEventListener("message", fn);
    },
    setTimer: (fn, ms) => window.setTimeout(fn, ms),
    clearTimer: (id) => window.clearTimeout(id as number),
    randomState,
    getJwt: () => useCartStore.getState().session.jwt,
    // Success and the login fallback both use a plain assign — never the hook — so there is no loop.
    navigate: (url) => window.location.assign(url),
  };

  // NEW MODE (customer SSO on, learned from the server's public bootstrap): a plain same-tab transfer through Accounts' own
  // relying-party start. No popup, no postMessage, no token in the page; the old bridge below is the feature-off path only.
  const ssoTransfer = (path: Parameters<typeof startSsoAccountTransfer>[1]): boolean => {
    if (useCartStore.getState().sso !== true || origin === window.location.origin) return false;
    startSsoAccountTransfer(io, path);
    return true;
  };

  const onClick = (ev: MouseEvent) => {
    if (ev.defaultPrevented || ev.button !== 0 || ev.metaKey || ev.ctrlKey || ev.shiftKey || ev.altKey) return;
    const a = (ev.target as Element | null)?.closest?.("a[href]") as HTMLAnchorElement | null;
    if (!a) return;
    const path = accountTargetOf(a.href, origin);
    if (!path) return;
    if (ssoTransfer(path)) {
      ev.preventDefault();
      return;
    }
    if (!needsBridge(origin)) return;
    ev.preventDefault();
    void startAccountHandoff(io, path);
  };

  const hook: AccountNavHook = (dest) => {
    const path = accountTargetOf(dest, origin);
    if (!path) return false;
    if (ssoTransfer(path)) return true;
    if (!needsBridge(origin)) return false;
    void startAccountHandoff(io, path);
    return true;
  };
  void useCartStore.getState().bootstrapSso();
  const w = window as unknown as { __SG_ACCOUNT_NAV?: AccountNavHook };
  w.__SG_ACCOUNT_NAV = hook;

  document.addEventListener("click", onClick, true);
  return () => {
    document.removeEventListener("click", onClick, true);
    if (w.__SG_ACCOUNT_NAV === hook) delete w.__SG_ACCOUNT_NAV;
    cancelActiveHandoff();
  };
}
